import React, {useState, useEffect} from 'react';
import { useTheme } from '@mui/material/styles';
import {Alert, Box, Button, Card, CardContent, CardHeader, Checkbox, Chip, CircularProgress, FormControlLabel, FormGroup, Grid2 as Grid, IconButton, Stack, Typography, useMediaQuery} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import Container from '../../components/Container';
import Main from '../../layouts/Main';
import SearchInput from './components/SearchInput';
import FilerList from './components/FilerList';
import DocumentList from './components/DocumentList';
import NewsList from './components/NewsList';
import ContributionList from './components/ContributionList';
import ShortFiler from '../../types/filers/shortFiler';
import { SearchDocument } from '../../types/search/searchDocument';
import { useErrorBoundary } from 'react-error-boundary';
import { ReceiptItem } from '../../types/reports/receiptItem';
import { ExpenditureItem } from '../../types/reports/expenditureItem';
import ExpenditureList from './components/ExpenditureList';
import LegalDocumentList from './components/LegalDocumentList';
import FormList from './components/FormList';
import { TrendingTopic } from '../../types/search/trendingTopics';

const Search = (): JSX.Element => {
  
  document.title = 'OCPF - Search Results';

  const theme = useTheme();
  const isSM = useMediaQuery(theme.breakpoints.down('sm'));
  const { showBoundary } = useErrorBoundary(); 
  const [isLoading, setIsLoading] = useState(true); 
  const searchInputWidth = isSM? '95%': '50%'; 

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const queryParams = new URLSearchParams(window.location.search);
  const initialSearchInput = queryParams.get('q') || '';
  const url = new URL(window.location.href);
  if(url.pathname.includes('/index') && initialSearchInput.length>0){
    window.location.assign(`/search?q=${initialSearchInput}`);
  }

  //trending search input
  const [topSearches, setTopSearches] = useState<TrendingTopic[]>();

  const [searchInput, setSearchInput] = useState<string>(initialSearchInput);
  const [reload, setReload] = useState<boolean>(false);
  const [filersResults, setFilersResults] = useState<ShortFiler[]>([]);
  const [filersCount, setFilersCount] = useState<number>(0);
  const [documentsResults, setDocumentsResults] = useState<SearchDocument[]>([]);
  const [documentsCount, setDocumentsCount] = useState<number>(0);
  const [legalDocumentsResults, setLegalDocumentsResults] = useState<SearchDocument[]>([]);
  const [legalDocumentsCount, setLegalDocumentsCount] = useState<number>(0);
  const [contributionsResults, setContributionsResults] = useState<ReceiptItem[]>([]);
  const [contributionsCount, setContributionsCount] = useState<number>(0);
  const [expendituresResults, setExpenditureResults] = useState<ExpenditureItem[]>([]);
  const [expendituresCount, setExpendituresCount] = useState<number>(0);
  const [newsResults, setNewsResults] = useState<SearchDocument[]>([]);
  const [newsCount, setNewsCount] = useState<number>(0);
  const [formsResults, setFormsResults] = useState<SearchDocument[]>([]);
  const [formsCount, setFormsCount] = useState<number>(0);
  const [notFound, setNotFound] = useState<boolean>(true);


  //filers
  const [searchFilters, setFilerFilters] = useState<string[]>([]);

  const updateSearchFilters = (event: React.ChangeEvent<HTMLInputElement>) => {
     const { name, checked } = event.target;
     if(checked){
        setFilerFilters([...searchFilters, name]);
     }
     else{
        setFilerFilters(searchFilters.filter((filter) => filter !== name));
     }
  }

  const clearSearchFilters = () => {
      setFilerFilters([]);
  }

  useEffect(() => {
        if(searchInput.length>0){
          getSearchResults();
        } 
        else{
          setIsLoading(false);
        } 
      }, [searchInput])

  const getSearchResults = async () => {

    setFilersCount(0);
    setDocumentsCount(0);

    try{
      let encodedSearchInput = encodeURIComponent(searchInput);
      //https://api.ocpf.us/filers/listings/A?searchPhrase=wu&excludeClosed=false
      //public/search/results?searchPhrase=${encodedSearchInput}
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/public/search/results?searchPhrase=${encodedSearchInput}`)
      .then(result => {
        if (!result.ok) {
          showBoundary(new Error('Failed to get filers and documents search results.'));
        }
        return result.json();
      })
      .then(result => {
        
        setFilersResults(result.filerResults);
        setFilersCount(result.filerResults.length);
        setDocumentsResults(result.nonLegalResults);
        setDocumentsCount(result.nonLegalResults.length);
        setLegalDocumentsResults(result.legalResults);
        setLegalDocumentsCount(result.legalResults.length);
        setNewsCount(result.newsResults.length);
        setNewsResults(result.newsResults);
        setFormsResults(result.formResults);
        setFormsCount(result.formResults.length);

        if(result.filerResults.length>0 || result.nonLegalResults.length>0 || result.legalResults.length>0){
          setNotFound(false);
        }

        //only do contribution and expenditure search if 5 or fewer words in search input
        const words = searchInput.trim().split(/\s+/).filter(Boolean);
        if(words.length<=5){
          getContributionSearchResults();
          getExpenditureSearchResults();
        }

        setIsLoading(false);
        getTrendingTopics();
      });
  
    }catch(error){
        console.error('Uncaught Error:', error);
    }
  }

  const getContributionSearchResults = async () => {

    setContributionsCount(0);

    try{
          await fetch(`${process.env.REACT_APP_API_BASE_URL}/search/items?searchTypeCategory=A&pagesize=10&sortField=&sortDirection=DESC&startIndex=1&name=${searchInput}&withSummary=true`)
          .then(result => {
            if (!result.ok) {
              showBoundary(new Error('Failed to get contributions search results.'));
            }
            return result.json();
          })
          .then(result => {
            setContributionsResults(result.items);
            setContributionsCount(result.summary.count);
            setIsLoading(false);
            if(result.length>0){
              setNotFound(false);
            }
          });
      
        }catch(error){
            console.error('Uncaught Error:', error);
        }
  }


  const getExpenditureSearchResults = async () => {

    setExpendituresCount(0);

    try{
          await fetch(`${process.env.REACT_APP_API_BASE_URL}/search/items?searchTypeCategory=B&pagesize=10&sortField=&sortDirection=DESC&startIndex=1&name=${searchInput}&withSummary=true`)
          .then(result => {
            if (!result.ok) {
              showBoundary(new Error('Failed to get expenditures search results.'));
            }
            return result.json();
          })
          .then(result => {
            setExpenditureResults(result.items);
            setExpendituresCount(result.summary.count);
            setIsLoading(false);
            if(result.length>0){
              setNotFound(false);
            }
          });
      
        }catch(error){
            console.error('Uncaught Error:', error);
        }
  }

  const updatetSearchInput = (newInput: string) => {
    setSearchInput(newInput);
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    params.set('q', newInput.toString());
    // Update the URL in the browser's history without reloading
    window.history.pushState({}, '', `${url.pathname}?${params.toString()}`);
  }

  const getTrendingTopics = async () => {
    try{
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/public/search/trending`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get the trending topics.'));
          }
          return result.json();
        })
        .then(result => {
          let count = result.length;
          if (count > 0){
             setTopSearches(result);
          }
        });
    }catch(error){
      console.error('Uncaught Error:', error);
      showBoundary(new Error(`${error}`));
    }
  }

  return (
    <Main>
      <Container maxWidth={{ sm: 720, md: 1440 }}>
      {/* Search Input*/}
      <Box
       display="flex" 
       justifyContent={'center'}
       marginBottom={5}
      >
        <Box 
          padding={1}
          width={searchInputWidth}
          component={Card}
          boxShadow={2}
          marginBottom={0}
          sx={{
            '& .MuiInputBase-input.MuiOutlinedInput-input': {
              bgcolor: 'background.paper',
              padding: '0 !important',
            },
          }}
          >
            <Box width={1} marginRight={1}>
              <SearchInput updateSearchInput={updatetSearchInput} searchInput={searchInput}></SearchInput>
            </Box>
        </Box>
      </Box>
      {/* Search Alert*/}
      {
        notFound &&
          <Grid container justifyContent="center" display={'flex'}>
            <Alert severity="warning" icon={false}>Your search returned no results. Please try again with a different phrase. If you still can’t find what you’re looking for? Email us at ocpf@mass.gov.</Alert>
          </Grid>      
      }
      {/* Search Results*/}
      {
        !notFound &&
        <Grid container spacing={4} marginTop={4}>
          <Grid size={{xs:12, md:2.5}}>
            <Card sx={{ boxShadow: 1 }}>
            {
              isSM &&
              <CardHeader
                action={
                  <IconButton aria-label="settings">
                    {
                      !isFilterOpen? <ExpandMoreIcon />: <ExpandLessIcon />
                    }
                  </IconButton>
                }
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                title="Filter Results"
                titleTypographyProps={{variant: 'subtitle2', fontWeight: 550, color: 'primary', fontStyle: 'italic'}}
              />
            }
            { (!isSM || isFilterOpen) &&
              <CardContent>
                <Box marginTop={3}>
                  <Typography paddingLeft={2} variant='subtitle1' fontWeight={550}>Filter By Type</Typography>
                </Box>
                <FormGroup sx={{padding: 2}}>
                {
                  filersCount > 0 && 
                  <FormControlLabel control={<Checkbox name="Filers" checked={searchFilters.includes("Filers")}  onChange={updateSearchFilters} />} label={`Filers [${filersCount}]`} />
                }
                {
                  documentsCount > 0 && 
                  <FormControlLabel control={<Checkbox name="General Info" checked={searchFilters.includes("General Info")} onChange={updateSearchFilters} />} label={`General Info [${documentsCount}]`} />
                }
                {
                  formsCount > 0 && 
                  <FormControlLabel control={<Checkbox name="Forms" checked={searchFilters.includes("Forms")} onChange={updateSearchFilters} />} label={`Forms (${formsCount})`} />
                }
                {
                  legalDocumentsCount > 0 &&
                  <FormControlLabel control={<Checkbox name="Legal" checked={searchFilters.includes("Legal")} onChange={updateSearchFilters} />} label={`Legal (${legalDocumentsCount})`} />
                }
                {
                  newsCount > 0 && 
                  <FormControlLabel control={<Checkbox name="News" checked={searchFilters.includes("News")} onChange={updateSearchFilters} />} label={`News (${newsCount})`} />
                }
                {
                  contributionsCount > 0 && 
                  <FormControlLabel control={<Checkbox name="Contributions" checked={searchFilters.includes("Contributions")} onChange={updateSearchFilters} />} label={`Contributions (${contributionsCount})`} />
                }
                {
                  expendituresCount > 0 && 
                  <FormControlLabel control={<Checkbox name="Expenditures" checked={searchFilters.includes("Expenditures")} onChange={updateSearchFilters} />} label={`Expenditures (${expendituresCount})`} />
                }
                </FormGroup>
                <Box margin={2}>
                  <Button variant="outlined" color="primary" onClick={clearSearchFilters}>Clear Filters</Button>
                </Box>
              </CardContent>
            }
            
            </Card>
            {
              !isSM &&
              <Box marginTop={2}>
                <Card sx={{ boxShadow: 1}}>
                    <CardContent>
                    <Box marginTop={3}>
                        <Typography paddingLeft={2} variant='subtitle1' fontWeight={550} >Trending</Typography>
                    </Box>
                    <Box padding={2}>
                      <Stack 
                        direction="row" 
                        spacing={1}  
                        alignItems={'flex-start'}
                        useFlexGap
                        flexWrap="wrap">
                        {
                          topSearches?.map((search) => (
                            <Chip label={search.description} key={search.id} variant="outlined" color="primary" onClick={()=>updatetSearchInput(search.description)}></Chip>
                          ))
                        }
                      </Stack>
                      </Box>
                    </CardContent>
                </Card>
              </Box>
            }
            
          </Grid>
          <Grid size={{xs:12, md:9.5}}>
            {
              filersResults.length > 0 && (searchFilters.length === 0 || searchFilters.includes('Filers')) &&
              <FilerList filers={filersResults}></FilerList>
            }
            {
              documentsResults.length > 0 && (searchFilters.length === 0 || searchFilters.includes('General Info')) &&
              <DocumentList documents={documentsResults} searchInput={searchInput}></DocumentList>
            }
            {
              formsResults.length > 0 && (searchFilters.length === 0 || searchFilters.includes('Forms')) &&
              <FormList documents={formsResults}></FormList>
            }
            {
              legalDocumentsResults.length > 0 && (searchFilters.length === 0 || searchFilters.includes('Legal')) &&
              <LegalDocumentList documents={legalDocumentsResults} searchInput={searchInput}></LegalDocumentList>
            }
            {
              contributionsResults.length >0 && (searchFilters.length === 0 || searchFilters.includes('Contributions')) &&
              <ContributionList items={contributionsResults} searchInput={searchInput} totalCount={contributionsCount}></ContributionList>
            }
            {
              expendituresResults.length >0 && (searchFilters.length === 0 || searchFilters.includes('Expenditures')) &&
              <ExpenditureList items={expendituresResults} searchInput={searchInput} totalCount={expendituresCount}></ExpenditureList>
            }
            {
              newsResults.length > 0 && (searchFilters.length === 0 || searchFilters.includes('News')) &&
              <NewsList newsPosts={newsResults}></NewsList>
            }
          </Grid>
        </Grid>
      }

      </Container>
       <CircularProgress
          size={50}
                  className={isLoading? "": "hidden"}
                  sx={{
                    position: "fixed",
                    left: "50%",
                    top: "20%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 2
          }}
      />
    </Main>
  );
};

export default Search;
